import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../../utils/dataTableShell";
import { StyledHeader, HeaderLeftContent } from "../../Common/styles/header";
import { DateTime } from "luxon";
import { useQuery } from "../../../utils/hooks";
import BackButton from "../../Common/UI/BackButton";

const CohortParticipants = () => {
  const navigate = useNavigate();
  const { id: studyId, groupId } = useParams();
  const cohortName = useQuery().get("name") || "";

  const [loading, setLoading] = useState<boolean>(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!studyId || !groupId) return;
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/participant-groups/${groupId}/members?page=${
            page + 1
          }&size=${pageSize}`
        );
        const { data } = res.data || {};
        const rows = data?.data || [];
        const mapped = rows.map((item: any) => ({
          id: item?.id,
          participantId: item?.participant?.id,
          subjectId: item?.participant?.subjectId,
          groupCode: item?.participant?.studyParticipant?.groupCode,
          displayId:
            item?.participant?.subjectId &&
            item?.participant?.studyParticipant?.groupCode
              ? `${item?.participant?.subjectId} - ${item?.participant?.studyParticipant?.groupCode}`
              : item?.participant?.subjectId ||
                item?.participant?.studyParticipant?.groupCode ||
                "",
          name: item?.participant?.firstName
            ? item?.participant?.lastName
              ? `${item?.participant?.firstName} ${item?.participant?.lastName}`
              : item?.participant?.firstName
            : "",
          email: item?.participant?.email || "",
          createdOn: item?.participant?.createdAt
            ? DateTime.fromISO(item?.participant?.createdAt).toFormat(
                "dd MMM yyyy"
              )
            : "",
          addedOn: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("dd MMM yyyy")
            : "",
        }));
        setParticipants(mapped);
        setTotalCount(data?.meta?.paginationOptions?.totalItems ?? 0);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [studyId, groupId, page]);

  const handleNavigate = (row: any) => {
    navigate(
      `/studies/${studyId}/participants/${row?.participantId}/responses?participant=${row.displayId}`
    );
  };
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <StyledHeader sx={{ borderBottom: "1px solid #E5E7EB" }}>
        <Box
          sx={{
            ...HeaderLeftContent,
            gap: 1,
          }}
        >
          <BackButton onClick={handleBack} />
          <Typography variant="h2" color="text.primary">
            {cohortName
              ? `${cohortName} Participants`
              : "User Group Participants"}
          </Typography>
        </Box>
      </StyledHeader>
      <Box sx={{ ...dataTablePageShell("calc(100% - 72px)"), p: 3 }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            footer={
              <DataTableFooter
                filteredCount={totalCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                loading={loading}
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ minWidth: 180 }}>
                    Participant ID
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 200 }}>Name</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 220 }}>Email</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 180 }}>
                    Created On
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: 180 }}>
                    Added On
                  </StyledTableCell>
                </TableRow>
              </TableHead>

              {!loading && totalCount > 0 && (
                <TableBody>
                  {participants.map((row) => (
                    <TableRow key={row.id}>
                      <StyledTableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleNavigate(row)}
                      >
                        {row.displayId || "-"}
                      </StyledTableCell>
                      <StyledTableCell>{row.name || "-"}</StyledTableCell>
                      <StyledTableCell>{row.email || "-"}</StyledTableCell>
                      <StyledTableCell>{row.createdOn || "-"}</StyledTableCell>
                      <StyledTableCell>{row.addedOn || "-"}</StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>

            {!loading && totalCount === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}

            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </DataTableLayout>
        </Box>
      </Box>
    </>
  );
};

export default CohortParticipants;
